import React from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Optional icon to show on the left */
  leftIcon?: React.ReactNode;
  /** Optional icon/element to show on the right */
  rightIcon?: React.ReactNode;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, rightIcon, error, errorMessage, ...props }, ref) => {
    return (
      <div className="relative w-full">
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              // Base styles - Institutional Editor
              // Recessed look: surface_container_lowest for "pit"
              'w-full rounded-lg border bg-surface-container-lowest text-on-surface',
              'font-normal text-sm font-sans',
              'transition-all duration-300',
              // Padding based on icons
              leftIcon ? 'pl-11' : 'pl-4',
              rightIcon ? 'pr-11' : 'pr-4',
              'py-2.5 min-h-[44px]',
              // Border: outline_variant at 10% opacity
              error ? 'border-accent-bearish' : 'border-border-subtle',
              // Focus: transition to primary at 50% opacity with subtle inner glow
              'focus:outline-none focus:border-[rgba(242,202,80,0.5)] focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.15),0_0_0_3px_rgba(242,202,80,0.15)]',
              // Inner shadow for depth (recessed effect)
              'shadow-[inset_0_2px_4px_rgba(0,0,0,0.15)]',
              // Hover
              'hover:border-border-subtle',
              // Placeholder
              'placeholder:text-text-muted',
              // Disabled
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-subtle',
              // Number input - hide spinners
              type === 'number' && [
                '[appearance:textfield]',
                '[&::-webkit-outer-spin-button]:appearance-none',
                '[&::-webkit-inner-spin-button]:appearance-none',
              ],
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
              {rightIcon}
            </div>
          )}
        </div>
        {error && errorMessage && (
          <p className="mt-1 text-xs text-accent-bearish">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
